import { Check, Plus, X } from "lucide-react";

import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
} from "ra-core";

import { useMemo, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Input } from "@/components/ui/input";

import { Separator } from "@/components/ui/separator";

import type { Company, Contact, Tag, Task, TaskContact } from "../types";

const CONTACT_TAG_NAMES = [
  "Заказчик",
  "Дизайнер",
  "Архитектор",
  "Подрядчик",
  "Монтаж",
  "Доставка",
  "Логистика",
  "Замер",
  "Производство",
  "Столяр",
  "Камень",
  "Стекло",
  "Металл",
  "Поставщик",
  "Фурнитура",
  "Материалы",
  "Оборудование",
  "Менеджер магазина",
];

const normalizeTagName = (value?: string | null) =>
  (value ?? "").trim().toLocaleLowerCase("ru-RU");

export const TaskContacts = ({
  task,
  canEdit,
  onChanged,
}: {
  task: Task;
  canEdit: boolean;
  onChanged?: () => void | Promise<void>;
}) => {
  const dataProvider = useDataProvider();

  const notify = useNotify();

  const queryClient = useQueryClient();

  const { identity } = useGetIdentity();

  const [addOpen, setAddOpen] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);

  const [search, setSearch] = useState("");

  const [isSaving, setIsSaving] = useState(false);

  const [firstName, setFirstName] = useState("");

  const [lastName, setLastName] = useState("");

  const [phone, setPhone] = useState("");

  const [companyId, setCompanyId] = useState("");

  const [newContactTagIds, setNewContactTagIds] = useState<number[]>([]);

  const {
    data: relations = [],
    isPending,
    refetch: refetchRelations,
  } = useGetList<TaskContact>("task_contacts", {
    pagination: {
      page: 1,
      perPage: 100,
    },

    sort: {
      field: "created_at",
      order: "ASC",
    },

    filter: {
      task_id: task.id,
    },
  });

  const contactIds = useMemo(
    () => relations.map((relation) => relation.contact_id),
    [relations],
  );

  const { data: selectedContacts = [] } = useGetMany<Contact>(
    "contacts",
    {
      ids: contactIds,
    },
    {
      enabled: contactIds.length > 0,
    },
  );

  const { data: searchResults = [] } = useGetList<Contact>(
    "contacts",
    {
      pagination: {
        page: 1,
        perPage: 20,
      },

      sort: {
        field: "last_name",
        order: "ASC",
      },

      filter: search.trim()
        ? {
            q: search.trim(),
          }
        : {},
    },
    {
      enabled: addOpen,
    },
  );

  const { data: companies = [] } = useGetList<Company>(
    "companies",
    {
      pagination: {
        page: 1,
        perPage: 100,
      },

      sort: {
        field: "name",
        order: "ASC",
      },

      filter: {},
    },
    {
      enabled: createOpen,
    },
  );

  const { data: tags = [] } = useGetList<Tag>(
    "tags",
    {
      pagination: {
        page: 1,
        perPage: 100,
      },

      sort: {
        field: "name",
        order: "ASC",
      },

      filter: {},
    },
    {
      enabled: createOpen,
    },
  );

  const allowedTagNames = useMemo(
    () => new Set(CONTACT_TAG_NAMES.map(normalizeTagName)),
    [],
  );

  const contactTags = tags.filter((tag) =>
    allowedTagNames.has(normalizeTagName(tag.name)),
  );

  const selectedIdSet = new Set(contactIds.map(String));

  const availableContacts = searchResults.filter(
    (contact) => !selectedIdSet.has(String(contact.id)),
  );

  const resetQuickCreate = () => {
    setFirstName("");
    setLastName("");
    setPhone("");
    setCompanyId("");
    setNewContactTagIds([]);
    setCreateOpen(false);
  };

  const refreshAfterChange = async () => {
    await refetchRelations();

    await queryClient.invalidateQueries({
      queryKey: ["task_contacts"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["contact_tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["tasks"],
    });

    await queryClient.invalidateQueries({
      queryKey: ["contacts"],
    });

    await onChanged?.();
  };

  const addContact = async (contactId: Identifier) => {
    setIsSaving(true);

    try {
      await dataProvider.create("task_contacts", {
        data: {
          task_id: task.id,

          contact_id: contactId,
        },
      });

      // Старое contact_id пока
      // сохраняем для совместимости
      // с остальным интерфейсом.
      if (task.contact_id == null) {
        await dataProvider.update("tasks", {
          id: task.id,

          data: {
            contact_id: contactId,
          },

          previousData: task,
        });
      }

      setSearch("");
      setAddOpen(false);

      await refreshAfterChange();

      notify("Контакт добавлен к задаче", {
        type: "success",
      });
    } catch {
      notify("Не удалось добавить контакт", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const removeContact = async (relation: TaskContact) => {
    setIsSaving(true);

    try {
      await dataProvider.delete("task_contacts", {
        id: relation.id,

        previousData: relation,
      });

      if (String(task.contact_id) === String(relation.contact_id)) {
        const replacement = relations.find((item) => item.id !== relation.id);

        await dataProvider.update("tasks", {
          id: task.id,

          data: {
            contact_id: replacement?.contact_id ?? null,
          },

          previousData: task,
        });
      }

      await refreshAfterChange();

      notify("Контакт убран из задачи", {
        type: "success",
      });
    } catch {
      notify("Не удалось убрать контакт", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleNewContactTag = (tagId: number) => {
    setNewContactTagIds((current) =>
      current.includes(tagId)
        ? current.filter((id) => id !== tagId)
        : [...current, tagId],
    );
  };

  const createContact = async () => {
    if (!firstName.trim()) {
      notify("Укажите имя контакта", {
        type: "warning",
      });

      return;
    }

    if (!identity) {
      return;
    }

    setIsSaving(true);

    try {
      const now = new Date().toISOString();

      const { data: newContact } = await dataProvider.create("contacts", {
        data: {
          first_name: firstName.trim(),

          last_name: lastName.trim(),

          company_id: companyId ? Number(companyId) : null,

          phone_jsonb: phone.trim()
            ? [
                {
                  number: phone.trim(),

                  type: "Work",
                },
              ]
            : [],

          email_jsonb: [],

          tags: newContactTagIds,

          first_seen: now,

          last_seen: now,

          has_newsletter: false,

          status: "new",

          background: "",

          sales_id: identity.id,
        },
      });

      await dataProvider.create("task_contacts", {
        data: {
          task_id: task.id,

          contact_id: newContact.id,
        },
      });

      if (task.contact_id == null) {
        await dataProvider.update("tasks", {
          id: task.id,

          data: {
            contact_id: newContact.id,
          },

          previousData: task,
        });
      }

      resetQuickCreate();

      setAddOpen(false);

      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
      });

      await refreshAfterChange();

      notify("Контакт создан и добавлен к задаче", {
        type: "success",
      });
    } catch {
      notify("Не удалось создать контакт", {
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">Контакты</div>

          <p className="mt-1 text-xs text-muted-foreground">
            Люди, связанные с этой задачей
          </p>
        </div>

        {canEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isSaving}
            onClick={() => {
              setAddOpen((value) => !value);

              setCreateOpen(false);
            }}
          >
            <Plus className="h-4 w-4" />
            Добавить
          </Button>
        )}
      </div>

      {!isPending && selectedContacts.length === 0 && (
        <div className="text-sm text-muted-foreground">
          Контакты не добавлены
        </div>
      )}

      {selectedContacts.length > 0 && (
        <div className="flex flex-col gap-2">
          {relations.map((relation) => {
            const contact = selectedContacts.find(
              (item) => String(item.id) === String(relation.contact_id),
            );

            if (!contact) {
              return null;
            }

            const primaryPhone = contact.phone_jsonb?.find((item) =>
              item.number?.trim(),
            )?.number;

            return (
              <div
                key={relation.id}
                className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {[contact.first_name, contact.last_name]
                      .filter(Boolean)
                      .join(" ")}
                  </div>

                  {(contact.title || primaryPhone) && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {[contact.title, primaryPhone]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                  )}
                </div>

                {canEdit && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isSaving}
                    className="h-7 w-7 shrink-0"
                    title="Убрать контакт из задачи"
                    onClick={() => removeContact(relation)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && addOpen && (
        <div className="rounded-lg border p-3">
          {!createOpen ? (
            <div className="flex flex-col gap-3">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Поиск по контактам..."
                autoFocus
              />

              <div className="max-h-56 overflow-y-auto">
                {availableContacts.map((contact) => {
                  const primaryPhone = contact.phone_jsonb?.find((item) =>
                    item.number?.trim(),
                  )?.number;

                  return (
                    <button
                      key={contact.id}
                      type="button"
                      disabled={isSaving}
                      onClick={() => addContact(contact.id)}
                      className="flex w-full items-start justify-between gap-3 rounded-md px-2 py-2 text-left hover:bg-muted"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {[contact.first_name, contact.last_name]
                            .filter(Boolean)
                            .join(" ")}
                        </div>

                        {(contact.title || primaryPhone) && (
                          <div className="text-xs text-muted-foreground">
                            {[contact.title, primaryPhone]
                              .filter(Boolean)
                              .join(" · ")}
                          </div>
                        )}
                      </div>

                      <Plus className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                    </button>
                  );
                })}

                {availableContacts.length === 0 && (
                  <div className="px-2 py-4 text-sm text-muted-foreground">
                    Ничего не найдено
                  </div>
                )}
              </div>

              <Separator />

              <Button
                type="button"
                variant="ghost"
                className="justify-start"
                onClick={() => setCreateOpen(true)}
              >
                <Plus className="h-4 w-4" />
                Новый контакт
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div>
                <div className="text-sm font-medium">Новый контакт</div>

                <p className="mt-1 text-xs text-muted-foreground">
                  После создания контакт сразу появится в справочнике
                </p>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <div className="mb-1 text-xs text-muted-foreground">
                    Имя *
                  </div>

                  <Input
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                </div>

                <div>
                  <div className="mb-1 text-xs text-muted-foreground">
                    Фамилия
                  </div>

                  <Input
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </div>
              </div>

              <div>
                <div className="mb-1 text-xs text-muted-foreground">
                  Телефон
                </div>

                <Input
                  value={phone}
                  onChange={(event) => setPhone(event.target.value)}
                  placeholder="+7..."
                />
              </div>

              <div>
                <div className="mb-1 text-xs text-muted-foreground">
                  Компания
                </div>

                <select
                  value={companyId}
                  onChange={(event) => setCompanyId(event.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                >
                  <option value="">Без компании</option>

                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              {contactTags.length > 0 && (
                <div>
                  <div className="mb-2 text-xs text-muted-foreground">Роли</div>

                  <div className="flex flex-wrap gap-2">
                    {contactTags.map((tag) => {
                      const selected = newContactTagIds.includes(tag.id);

                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => toggleNewContactTag(tag.id)}
                          className={
                            selected
                              ? "rounded-full ring-2 ring-foreground/20"
                              : "rounded-full opacity-75 hover:opacity-100"
                          }
                        >
                          <Badge
                            variant="secondary"
                            className="cursor-pointer gap-1.5 font-normal rovero-colored-tag text-[#482719]"
                            style={{
                              backgroundColor: tag.color,
                            }}
                          >
                            {selected && <Check className="h-3.5 w-3.5" />}

                            {tag.name}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  onClick={() => {
                    resetQuickCreate();
                  }}
                >
                  Назад
                </Button>

                <Button
                  type="button"
                  disabled={isSaving || !firstName.trim()}
                  onClick={createContact}
                >
                  Создать и добавить
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
